// Google Analytics 4 (GA4) Configuration

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
    fbq?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
  }
}

// Meta Pixel helper. The pixel is initialised in index.html; this only fires
// events. Safe to call before fbq has loaded — the snippet queues calls.
export const trackMetaEvent = (
  event: string,
  params?: Record<string, any>,
  standard: boolean = true
) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(standard ? 'track' : 'trackCustom', event, params);
  }
};

// GA4 Measurement ID
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-06T7XSKFVG';

// Google Ads Conversion ID and Labels
const GOOGLE_ADS_ID = 'AW-17866478038';

// Conversion Labels for different actions
const CONVERSION_LABELS = {
  CALENDLY_BOOKING: '-P81COPn2uwbENajs8dC',       // £150 - Primary booking
  PHONE_CALL: 'qB6YCIX1k_wbENajs8dC',             // £100 - Phone call lead
  EMAIL_CLICK: '5UE_CML2k_wbENajs8dC',            // £50 - Email click
  FREE_CONSULTATION: 'JuJRCL7NovwbENajs8dC',      // £75 - Free consultation booking
  WHATSAPP_CLICK: '0tojCImtl_wbENajs8dC',         // £80 - WhatsApp contact
};

// Default booking label (backwards compatibility)
const GOOGLE_ADS_CONVERSION_LABEL = CONVERSION_LABELS.CALENDLY_BOOKING;

// NOTE: there is deliberately no initGA() here. gtag.js is loaded and both
// destinations (GA4 G-06T7XSKFVG and Google Ads AW-17866478038) are configured
// in index.html. A second loader in here would double-count every page view.

// Single source of truth for Sharon's contact points.
// 07932139245 is her business mobile and is also her WhatsApp number — Terms
// ("Cancellations must be made by WhatsApp or email") and the Privacy Policy
// both already state she takes WhatsApp on it.
export const CONTACT = {
  phone: '07932139245',
  phoneIntl: '447932139245',
  email: 'Sharon@sharonclare-hypnotherapy.com',
};

export const whatsappHref = (message?: string) =>
  `https://wa.me/${CONTACT.phoneIntl}` +
  (message ? `?text=${encodeURIComponent(message)}` : '');

// Track page views
export const trackPageView = (path: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_location: window.location.href,
    });
  }
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track form submissions
export const trackFormSubmit = (formName: string, formType: string) => {
  trackEvent('form_submit', formType, formName);
  trackMetaEvent('Lead', { content_name: formName, content_category: formType });
};

// Track button clicks
export const trackButtonClick = (buttonName: string, buttonLocation: string) => {
  trackEvent('click', 'button', `${buttonName} - ${buttonLocation}`);
};

// Track newsletter subscriptions
export const trackNewsletter = (source: string) => {
  trackEvent('sign_up', 'newsletter', source);
};

// Track poll submissions
export const trackPoll = (pollName: string, choice: string) => {
  trackEvent('poll_submit', pollName, choice);
};

// Track consultation bookings (GA4 event only - conversion tracked separately with enhanced data)
export const trackBooking = (method: string, location?: string) => {
  trackEvent('generate_lead', 'booking', `${method}${location ? ` - ${location}` : ''}`);

  // Note: Google Ads conversion is tracked separately with enhanced user data
  // See BookingForm.tsx for conversion tracking implementation
};

// Hash function for enhanced conversions (simple SHA-256 would be better but this is a fallback)
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Track Google Ads conversion with £150 value and optional enhanced conversion data
export const trackGoogleAdsConversion = (
  value: number = 150,
  currency: string = 'GBP',
  userData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    address?: {
      city?: string;
      region?: string;
      postalCode?: string;
      country?: string;
    };
  }
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Fire conversion event to Google Ads
    if (GOOGLE_ADS_CONVERSION_LABEL) {
      const transactionId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const conversionData: any = {
        'send_to': `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
        'value': value,
        'currency': currency,
        'transaction_id': transactionId
      };

      // Add enhanced conversion data if provided (improves attribution)
      if (userData) {
        const enhancedData: any = {};

        if (userData.email) {
          enhancedData.email = userData.email.toLowerCase().trim();
        }
        if (userData.phone) {
          enhancedData.phone_number = userData.phone.replace(/\D/g, ''); // Remove non-digits
        }
        if (userData.firstName) {
          enhancedData.first_name = userData.firstName.toLowerCase().trim();
        }
        if (userData.lastName) {
          enhancedData.last_name = userData.lastName.toLowerCase().trim();
        }
        if (userData.address) {
          if (userData.address.city) enhancedData.city = userData.address.city.toLowerCase().trim();
          if (userData.address.region) enhancedData.region = userData.address.region.toLowerCase().trim();
          if (userData.address.postalCode) enhancedData.postal_code = userData.address.postalCode.toUpperCase().trim();
          if (userData.address.country) enhancedData.country = userData.address.country.toUpperCase().trim();
        }

        // Only add if we have data
        if (Object.keys(enhancedData).length > 0) {
          // Set enhanced conversion data
          window.gtag('set', 'user_data', enhancedData);
          console.log('Enhanced conversion data set (user data will be hashed by Google)');
        }
      }

      // Fire the conversion
      window.gtag('event', 'conversion', conversionData);

      trackMetaEvent('Schedule', { content_name: 'Calendly booking', value, currency });

      console.log('✅ Google Ads conversion tracked:', {
        value,
        currency,
        transactionId,
        hasEnhancedData: !!userData
      });
    } else {
      console.warn('⚠️ Google Ads conversion label not configured. Set VITE_GOOGLE_ADS_CONVERSION_LABEL in .env');
      console.warn('Conversion would have tracked: £' + value + ' ' + currency);
    }
  }
};

// Track phone call conversions
export const trackPhoneCall = (phoneNumber: string = '07932139245') => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Track in GA4
    window.gtag('event', 'phone_call', {
      event_category: 'engagement',
      event_label: phoneNumber,
      value: 100
    });

    // Track in Google Ads with dedicated Phone Call conversion
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.PHONE_CALL}`,
      'value': 100,
      'currency': 'GBP',
      'transaction_id': `phone_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });
    trackMetaEvent('Contact', { content_name: 'Phone call', value: 100, currency: 'GBP' });
    console.log('📞 Phone call conversion tracked - £100');
  }
};

// Track email click conversions
export const trackEmailClick = (emailAddress: string = 'Sharon@sharonclare-hypnotherapy.com') => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Track in GA4
    window.gtag('event', 'email_click', {
      event_category: 'engagement',
      event_label: emailAddress,
      value: 50
    });

    // Track in Google Ads
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.EMAIL_CLICK}`,
      'value': 50,
      'currency': 'GBP',
      'transaction_id': `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });
    trackMetaEvent('Contact', { content_name: 'Email click', value: 50, currency: 'GBP' });
    console.log('📧 Email click conversion tracked - £50');
  }
};

// Track WhatsApp click conversions
export const trackWhatsAppClick = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Track in GA4
    window.gtag('event', 'whatsapp_click', {
      event_category: 'engagement',
      event_label: 'WhatsApp Contact',
      value: 80
    });

    // Track in Google Ads
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.WHATSAPP_CLICK}`,
      'value': 80,
      'currency': 'GBP',
      'transaction_id': `whatsapp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });
    trackMetaEvent('Contact', { content_name: 'WhatsApp click', value: 80, currency: 'GBP' });
    console.log('💬 WhatsApp click conversion tracked - £80');
  }
};

// Track free consultation booking
export const trackFreeConsultation = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Track in GA4
    window.gtag('event', 'free_consultation', {
      event_category: 'booking',
      event_label: 'Free Consultation',
      value: 75
    });

    // Track in Google Ads
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.FREE_CONSULTATION}`,
      'value': 75,
      'currency': 'GBP',
      'transaction_id': `free_consult_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });
    trackMetaEvent('Schedule', { content_name: 'Free consultation', value: 75, currency: 'GBP' });
    console.log('📅 Free consultation conversion tracked - £75');
  }
};

// Track outbound links
export const trackOutboundLink = (url: string, linkText: string) => {
  trackEvent('click', 'outbound_link', `${linkText} - ${url}`);
  return false; // Return false to delay navigation for tracking
};

// Track audio downloads
export const trackAudioDownload = (audioName: string, audioUrl: string) => {
  trackEvent('file_download', 'audio', audioName);
  trackOutboundLink(audioUrl, audioName);
};

export default {
  initContactLinkTracking,
  trackMetaEvent,
  CONTACT,
  whatsappHref,
  trackPageView,
  trackEvent,
  trackFormSubmit,
  trackButtonClick,
  trackNewsletter,
  trackPoll,
  trackBooking,
  trackGoogleAdsConversion,
  trackPhoneCall,
  trackEmailClick,
  trackWhatsAppClick,
  trackFreeConsultation,
  trackOutboundLink,
  trackAudioDownload,
};


// ---------------------------------------------------------------------------
// Delegated contact-link tracking
// ---------------------------------------------------------------------------
// The site has 177 `tel:` links and 13 `mailto:` links spread across service,
// location and blog pages. Only 4 of them ever had an onClick handler, so the
// Phone Call (£100) and Email Click (£50) Google Ads conversions were firing
// from the header/footer only, and the WhatsApp (£80) conversion could never
// fire at all because no WhatsApp link existed.
//
// Rather than hand-editing ~190 anchors (and needing to remember on every new
// page), one capture-phase listener on the document catches every contact link
// — including ones added later. Capture phase means it still runs if a
// component calls stopPropagation, and it never calls preventDefault, so
// navigation is untouched.
//
// This is the ONLY place phone/email/WhatsApp clicks are tracked. Do not add
// per-anchor onClick handlers as well or conversions will double-count.
let contactLinkTrackingInstalled = false;

export const initContactLinkTracking = () => {
  if (typeof document === 'undefined' || contactLinkTrackingInstalled) return;
  contactLinkTrackingInstalled = true;

  document.addEventListener(
    'click',
    (event) => {
      const el = event.target as Element | null;
      if (!el || typeof el.closest !== 'function') return;

      const anchor = el.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = (anchor.getAttribute('href') || '').trim();
      if (!href) return;

      if (/^tel:/i.test(href)) {
        trackPhoneCall(href.replace(/^tel:/i, '').trim() || CONTACT.phone);
      } else if (/^mailto:/i.test(href)) {
        trackEmailClick(href.replace(/^mailto:/i, '').split('?')[0].trim() || CONTACT.email);
      } else if (/^https?:\/\/(wa\.me|(api|web)\.whatsapp\.com)/i.test(href)) {
        trackWhatsAppClick();
      }
    },
    true
  );
};
